import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { newId } from '@terra/utils';

import { ApiClientError, isApiErrorBody } from './errors';
import type { TokenStore } from './token-store';

export interface HttpConfig {
  baseURL: string;
  tokenStore: TokenStore;
  /** Called when a refresh attempt fails (or none is configured) so the app can log the user out. */
  onAuthFailure?: () => void;
  /** Performs a token refresh against the API; resolves to the new access token or null. */
  refreshAccessToken?: () => Promise<string | null>;
  /** Default request timeout in ms. */
  timeoutMs?: number;
  /** Send cookies with requests (needed for the httpOnly refresh-token cookie). */
  withCredentials?: boolean;
}

/** The sliver of `localStorage` used below, so no DOM lib is needed. */
interface WebStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retriedAuth?: boolean;
}

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Every mutating request carries an Idempotency-Key (ADR-0003).
 *
 * This used to be a hand-kept list of the paths that require one, and the
 * server grew ~45 such endpoints while the list stayed at a handful — so
 * creating a support ticket, withdrawing from the wallet, requesting a payout
 * and most admin actions failed with IDEMPOTENCY_KEY_REQUIRED from every app.
 * Endpoints without the middleware ignore the header, and the key is fixed
 * per request config, so the auth-refresh retry replays safely.
 */
function needsIdempotencyKey(config: InternalAxiosRequestConfig): boolean {
  return MUTATING_METHODS.has((config.method ?? 'get').toLowerCase());
}

export function createHttp(cfg: HttpConfig): AxiosInstance {
  const instance = axios.create({
    baseURL: cfg.baseURL,
    timeout: cfg.timeoutMs ?? 20_000,
    withCredentials: cfg.withCredentials ?? true,
    headers: { Accept: 'application/json' },
  });

  // ---- request: auth header + request id + idempotency key ----
  instance.interceptors.request.use((config) => {
    const token = cfg.tokenStore.getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
    config.headers.set('X-Request-Id', newId());
    if (needsIdempotencyKey(config) && !config.headers.has('Idempotency-Key')) {
      config.headers.set('Idempotency-Key', newId());
    }

    // Identify a guest so a cart survives until they sign in. Reached through
    // `globalThis` and typed locally rather than through `window`, because this
    // module is also compiled for React Native, which has no DOM types and no
    // localStorage — there the block simply does not run.
    const store = (globalThis as { localStorage?: WebStorage }).localStorage;
    if (store) {
      try {
        let anonId = store.getItem('terra_anonymous_id');
        if (!anonId) {
          anonId = `anon_${newId()}`;
          store.setItem('terra_anonymous_id', anonId);
        }
        config.headers.set('X-Anonymous-Id', anonId);
      } catch {
        // Private mode, or site data blocked. A guest without a stable id is a
        // guest whose cart does not follow them — not a request that should fail.
      }
    }

    return config;
  });

  // ---- response: normalise errors + transparent 401 refresh-and-retry ----
  let refreshInFlight: Promise<string | null> | null = null;

  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        throw new ApiClientError({
          code: 'UNKNOWN',
          message: 'Unexpected client error',
          status: 0,
        });
      }
      const axiosErr = error as AxiosError;
      const original = axiosErr.config as RetriableConfig | undefined;

      // network / timeout / cancel — no HTTP response
      if (!axiosErr.response) {
        if (axiosErr.code === 'ECONNABORTED' || axiosErr.code === 'ETIMEDOUT') {
          throw new ApiClientError({
            code: 'TIMEOUT',
            message:
              'The server took too long to answer. The action may still have gone through — refresh the page to check before trying again.',
            status: 0,
          });
        }
        if (axios.isCancel(error)) {
          throw new ApiClientError({
            code: 'CANCELLED',
            message: 'Request was cancelled',
            status: 0,
          });
        }
        const offline =
          (globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine === false;
        throw new ApiClientError({
          code: 'NETWORK_ERROR',
          message: offline
            ? 'You are offline. Check your internet connection and try again.'
            : 'Could not reach the Terra Shoppy server — it may be restarting or your connection dropped. Please try again in a moment.',
          status: 0,
        });
      }

      const { status, data } = axiosErr.response;

      // A 401 from the refresh endpoint itself means the refresh token is gone
      // or expired. Answering it by calling refresh again just asks the same
      // question twice and gets the same no — every anonymous page load fired
      // two /auth/refresh requests and logged two 401s to the console because
      // of it. There is nothing to retry with, so fail straight through.
      const isRefreshCall = (original?.url ?? '').includes('/auth/refresh');

      // 401 → try one refresh, then replay the original request
      if (
        status === 401 &&
        original &&
        !original._retriedAuth &&
        !isRefreshCall &&
        cfg.refreshAccessToken
      ) {
        original._retriedAuth = true;
        try {
          refreshInFlight ??= cfg.refreshAccessToken().finally(() => {
            refreshInFlight = null;
          });
          const newToken = await refreshInFlight;
          if (newToken) {
            cfg.tokenStore.setAccessToken(newToken);
            original.headers.set('Authorization', `Bearer ${newToken}`);
            return instance.request(original);
          }
        } catch (refreshErr) {
          // Only a refresh the server *refused* ends the session. A refresh that
          // never got an answer — offline, a timeout, the server restarting —
          // says nothing about the session, and signing the user out for it is
          // how a phone on a weak signal kept logging people out.
          const refused =
            axios.isAxiosError(refreshErr) &&
            (refreshErr.response?.status === 401 || refreshErr.response?.status === 403);
          if (!refused) {
            throw new ApiClientError({
              code: 'NETWORK_ERROR',
              message:
                'Could not reach the Terra Shoppy server — check your connection and try again.',
              status: 0,
            });
          }
        }
        cfg.tokenStore.setAccessToken(null);
        cfg.onAuthFailure?.();
      } else if (status === 401) {
        cfg.tokenStore.setAccessToken(null);
        cfg.onAuthFailure?.();
      }

      if (isApiErrorBody(data)) {
        const errArgs: ConstructorParameters<typeof ApiClientError>[0] = {
          code: data.error.code,
          message: data.error.message,
          status,
        };
        if (data.error.details !== undefined) errArgs.details = data.error.details;
        if (data.error.requestId !== undefined) errArgs.requestId = data.error.requestId;
        throw new ApiClientError(errArgs);
      }
      // No error envelope: a proxy or gateway answered instead of the API.
      throw new ApiClientError({
        code: 'UNKNOWN',
        message:
          status === 502 || status === 503 || status === 504
            ? 'The server is temporarily unavailable (it may be restarting). Please try again in a minute.'
            : status === 413
              ? 'That upload is too large. Use a smaller file.'
              : `The server returned an unexpected error (HTTP ${status}). Please try again.`,
        status,
      });
    },
  );

  return instance;
}
